import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    getUsersPaginatedThunk,
    selectUsers,
    selectUsersLoading,
    selectUsersNextPage
} from '../../app/userSlice';
import MenuItemsLoader from '../../components/loading/menuLoader';
import { SportradarUser } from '../../interfaces/sportradarUser';
import { UserItem } from './components/userItem';
import { CustomEvent } from '@piwikpro/react-piwik-pro';

import './index.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

export function UsersPage(): React.ReactNode {
    const dispatch = useAppDispatch();
    const users = useAppSelector(selectUsers);
    const usersLoading = useAppSelector(selectUsersLoading);
    const nextPageToken = useAppSelector(selectUsersNextPage);
    const { t } = useTranslation();

    const [usersCopy, setUsersCopy] = useState<SportradarUser[]>();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [pageHistory, setPageHistory] = useState<(string | undefined)[]>([
        undefined
    ]);
    const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

    useEffect(() => {
        dispatch(getUsersPaginatedThunk({ pageSize }));
        CustomEvent.trackEvent('page_view', 'Users');
    }, []);

    useEffect(() => {
        setUsersCopy(users?.filter((user) => user.email.includes(searchTerm)));
    }, [users]);

    if (!users && usersLoading) {
        return <MenuItemsLoader />;
    }

    const handleSearchUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setUsersCopy(
            users?.filter((user) =>
                user.email.includes(event.target.value.toLowerCase())
            )
        );
    };

    const handlePageSizeChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const newSize = Number(event.target.value);
        setPageSize(newSize);
        setPageHistory([undefined]);
        setCurrentPageIndex(0);
        dispatch(getUsersPaginatedThunk({ pageSize: newSize }));
    };

    const handleNextPage = () => {
        if (!nextPageToken) return;
        const newHistory = [
            ...pageHistory.slice(0, currentPageIndex + 1),
            nextPageToken
        ];
        setPageHistory(newHistory);
        setCurrentPageIndex(currentPageIndex + 1);
        dispatch(getUsersPaginatedThunk({ pageSize, nextPage: nextPageToken }));
    };

    const handlePreviousPage = () => {
        if (currentPageIndex === 0) return;
        const prevIndex = currentPageIndex - 1;
        setCurrentPageIndex(prevIndex);
        dispatch(
            getUsersPaginatedThunk({
                pageSize,
                nextPage: pageHistory[prevIndex]
            })
        );
    };

    const isFirstPage = currentPageIndex === 0;
    const isLastPage = !nextPageToken;

    return (
        <div className='users'>
            <div className='users-title'>{t('users')}</div>
            <div className='search-users'>
                <input
                    className='input-form'
                    type='text'
                    value={searchTerm}
                    onChange={handleSearchUsers}
                />
            </div>
            <div className='users-header'>
                <div className='email'>{t('email')}</div>
                <div className='employee-code'>{t('employee_code')}</div>
                <div className='direct-payment'>{t('direct_payment')}</div>
                <div className='action'>{t('actions')}</div>
            </div>
            {usersLoading ? (
                <MenuItemsLoader />
            ) : (
                usersCopy?.map((user) => (
                    <UserItem user={user} key={user.email} />
                ))
            )}
            <div className='pagination-controls'>
                <div className='pagination-page-size'>
                    <label htmlFor='page-size-select'>{t('page_size')}:</label>
                    <select
                        id='page-size-select'
                        className='page-size-select'
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </div>
                <div className='pagination-nav'>
                    <button
                        className='btn btn-gray pagination-btn'
                        onClick={handlePreviousPage}
                        disabled={isFirstPage || usersLoading}
                    >
                        {t('previous')}
                    </button>
                    <span className='pagination-page-info'>
                        {t('page')} {currentPageIndex + 1}
                    </span>
                    <button
                        className='btn btn-gray pagination-btn'
                        onClick={handleNextPage}
                        disabled={isLastPage || usersLoading}
                    >
                        {t('next')}
                    </button>
                </div>
            </div>
        </div>
    );
}
